/**
 * AuthModal - Email-based authentication for Piano With Pan
 *
 * Features:
 * - Gmail OAuth (fast sign-in)
 * - Email OTP (magic link alternative)
 * - Pan character guidance
 * - Mobile responsive
 * - Optional close button (can be hidden to force login)
 * - Optional "Continue without logging in" button
 * - ✅ V2: Google Analytics tracking on every key interaction
 * - ✅ V3: Meta Pixel tracking added
 * - ✅ V4: pixelSignUp now only fires on genuine NEW accounts, not every
 *          returning login — prevents polluting Meta's Lookalike Audience
 *          data with repeat-login noise (see fireAuthSuccess below)
 *
 * Flow:
 * 1. User sees "Continue with Gmail" or "Continue with Email"
 * 2. Gmail → Instant auth → Close modal
 * 3. Email → Enter email → Send OTP → Verify → Close modal
 *
 * Usage:
 * <AuthModal
 *   onSuccess={(user) => handleAuthSuccess(user)}
 *   onClose={() => setShowAuthModal(false)}
 *   message="Sign in to save your progress!"
 *   hideCloseButton={false}        // Set to true to force login
 *   showContinueWithoutLogin={false}
 *   onContinueWithoutLogin={() => handleSkip()}
 *   gaSource="level_1_start_gate"  // ✅ Where the modal opened (for GA)
 * />
 */

import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';

// ✅ GA helpers
import { sendGAEvent, trackSignUp } from '../analytics/analytics';
// ✅ META PIXEL ADDED
import { pixelSignUp } from '../analytics/metaPixel';

export default function AuthModal({
  onSuccess = () => {},
  onClose = () => {},
  message = "Let's save your progress!",
  showBenefits = true,
  hideCloseButton = false,
  showContinueWithoutLogin = false,
  onContinueWithoutLogin = null,
  // ✅ Optional: caller can pass a label so we know WHERE the modal was opened
  gaSource = 'unknown',
}) {
  const [mode, setMode] = useState('select'); // 'select', 'email-input', 'otp-verify'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ✅ Make sure we don't double-fire success / view events
  const successFiredRef = useRef(false);
  const viewFiredRef = useRef(false);

  /**
   * ✅ GA: Modal opened (fires once)
   */
  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    sendGAEvent('auth_modal_view', {
      source: gaSource,
      force_login: hideCloseButton === true,
    });
  }, [gaSource, hideCloseButton]);

  /**
   * ✅ GA: Modal closed via X (only when close is allowed)
   */
  const handleCloseClick = () => {
    sendGAEvent('auth_modal_dismissed', { source: gaSource });
    onClose();
  };

  /**
   * ✅ GA: User chose to continue without logging in
   */
  const handleSkipLogin = () => {
    sendGAEvent('auth_continue_without_login', { source: gaSource });
    if (typeof onContinueWithoutLogin === 'function') {
      onContinueWithoutLogin();
    }
  };

  /**
   * ✅ GA + META PIXEL: When auth succeeds (call this from every success path)
   * Only fires pixelSignUp (Meta's CompleteRegistration) for genuinely NEW
   * accounts — not every returning login. Supabase's passwordless OTP flow
   * returns the same success shape whether it's someone's 1st or 100th
   * login, so we detect "new" by comparing created_at to last_sign_in_at:
   * if they're within a few seconds of each other, this session created
   * the account just now.
   */
  const fireAuthSuccess = (user, method) => {
    if (successFiredRef.current) return;
    successFiredRef.current = true;

    // We use trackSignUp for both first-time + returning logins.
    // GA4 will dedupe by user_id once you set it. For now this gives
    // you a single event you can filter by `method` and `source`.
    trackSignUp(method);

    // ✅ Only fire Meta's CompleteRegistration for true new signups —
    // firing it on every returning login pollutes the Lookalike Audience
    // Meta builds from this event.
    const createdAt = user?.created_at ? new Date(user.created_at).getTime() : null;
    const lastSignInAt = user?.last_sign_in_at ? new Date(user.last_sign_in_at).getTime() : null;
    const isNewAccount =
      createdAt != null && lastSignInAt != null && Math.abs(lastSignInAt - createdAt) < 10000; // within 10s

    if (isNewAccount) {
      pixelSignUp(method); // ← META PIXEL: fires only for real new accounts
    }

    // Extra event with the source context (where the modal was opened from)
    sendGAEvent('login_success', {
      method,
      source: gaSource,
      is_new_account: isNewAccount,
    });

    onSuccess(user);
  };

  /**
   * Handle Gmail OAuth
   */
  const handleGmailAuth = async () => {
    try {
      setLoading(true);
      setError('');

      // ✅ GA: user clicked Gmail (this navigates away, so log it BEFORE)
      sendGAEvent('auth_method_clicked', {
        method: 'google',
        source: gaSource,
      });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;

      // OAuth redirects automatically, so user leaves this page.
      // Final login_success will be fired by AuthCallback.jsx (when you
      // wire that up next), so we don't fire it here.
    } catch (err) {
      console.error('Gmail auth error:', err);
      setError(err.message || 'Gmail sign-in failed. Please try email instead.');
      setLoading(false);

      // ✅ GA: Gmail sign-in failed
      sendGAEvent('auth_error', {
        method: 'google',
        source: gaSource,
        error_message: String(err?.message || '').slice(0, 120),
      });
    }
  };

  /**
   * Send OTP to email
   */
  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ GA: user submitted email → OTP requested
      sendGAEvent('auth_otp_requested', { source: gaSource });

      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // ✅ GA: OTP successfully sent
      sendGAEvent('auth_otp_sent', { source: gaSource });

      // Success - show OTP input
      setMode('otp-verify');
      setLoading(false);
    } catch (err) {
      console.error('OTP send error:', err);
      setError(err.message || 'Failed to send code. Please try again.');
      setLoading(false);

      // ✅ GA: failed to send OTP
      sendGAEvent('auth_error', {
        method: 'email_otp_send',
        source: gaSource,
        error_message: String(err?.message || '').slice(0, 120),
      });
    }
  };

  /**
   * Verify OTP code
   */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ GA: user submitted the 6-digit code
      sendGAEvent('auth_otp_submitted', { source: gaSource });

      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otp.trim(),
        type: 'email',
      });

      if (error) throw error;

      // Success! User is now logged in
      const user = data?.user;
      if (user) {
        // ✅ GA + META PIXEL: success
        fireAuthSuccess(user, 'email_otp');
      }
    } catch (err) {
      console.error('OTP verify error:', err);
      setError(err.message || 'Invalid code. Please try again.');
      setLoading(false);

      // ✅ GA: OTP verify failed (wrong code, expired, etc.)
      sendGAEvent('auth_error', {
        method: 'email_otp_verify',
        source: gaSource,
        error_message: String(err?.message || '').slice(0, 120),
      });
    }
  };

  /**
   * Render: Auth method selection
   */
  const renderSelect = () => (
    <>
      {/* Header with Pan */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <img
            src="/images/pan-the-panda.png"
            alt="Pan the Panda"
            className="w-20 h-20 object-contain drop-shadow-lg"
          />
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-2">
          🐼 Pan says: "{message}"
        </h2>
        <p className="text-sm text-gray-600">
          Sign in to unlock your daily practice time
        </p>
      </div>

      {/* Benefits (optional) */}
      {showBenefits && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
          <p className="text-xs font-bold text-purple-900 mb-2">
            Why sign in?
          </p>
          <ul className="space-y-1.5 text-xs text-purple-800">
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">✓</span>
              <span>Save your progress across devices</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">✓</span>
              <span>Track your daily practice time</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">✓</span>
              <span>Unlock achievements & rewards</span>
            </li>
          </ul>
        </div>
      )}

      {/* Gmail Button */}
      <button
        onClick={handleGmailAuth}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-xl font-bold text-gray-800 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {loading ? 'Signing in...' : 'Continue with Gmail'}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-gray-300" />
        <span className="text-sm text-gray-500 font-medium">or</span>
        <div className="flex-1 h-px bg-gray-300" />
      </div>

      {/* Email Button */}
      <button
        onClick={() => {
          // ✅ GA: user picked the email path
          sendGAEvent('auth_method_clicked', {
            method: 'email',
            source: gaSource,
          });
          setMode('email-input');
        }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-purple-500 hover:bg-purple-600 rounded-xl font-bold text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-xl">📧</span>
        Continue with Email
      </button>

      {/* Continue without logging in (optional) */}
      {showContinueWithoutLogin && onContinueWithoutLogin && (
        <button
          onClick={handleSkipLogin}
          className="w-full mt-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-bold transition-all"
        >
          Continue without logging in →
        </button>
      )}

      {/* Error display */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
    </>
  );

  /**
   * Render: Email input
   */
  const renderEmailInput = () => (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <img
            src="/images/pan-the-panda.png"
            alt="Pan the Panda"
            className="w-16 h-16 object-contain drop-shadow-lg"
          />
        </div>
        <h2 className="text-xl font-black text-gray-900 mb-1">
          Enter Your Email 📧
        </h2>
        <p className="text-sm text-gray-600">
          We'll send you a 6-digit code to sign in
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSendOTP} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            autoFocus
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none text-base"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              sendGAEvent('auth_back_clicked', {
                from: 'email-input',
                source: gaSource,
              });
              setMode('select');
              setEmail('');
              setError('');
            }}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-bold text-gray-700 transition-all"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl font-bold text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : 'Send Code'}
          </button>
        </div>
      </form>
    </>
  );

  /**
   * Render: OTP verification
   */
  const renderOTPVerify = () => (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <img
            src="/images/pan-the-panda.png"
            alt="Pan the Panda"
            className="w-16 h-16 object-contain drop-shadow-lg"
          />
        </div>
        <h2 className="text-xl font-black text-gray-900 mb-1">
          Check Your Email! 📬
        </h2>
        <p className="text-sm text-gray-600 mb-2">
          We sent a 6-digit code to:
        </p>
        <p className="text-sm font-bold text-purple-600">{email}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleVerifyOTP} className="space-y-4">
        <div>
          <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 mb-2">
            Enter the 6-digit code
          </label>
          <input
            id="otp"
            type="text"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtp(value);
            }}
            placeholder="123456"
            required
            autoFocus
            maxLength={6}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none text-center text-2xl font-bold tracking-widest"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Resend link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              // ✅ GA: user wants to retry / change email
              sendGAEvent('auth_otp_retry_clicked', { source: gaSource });
              setOtp('');
              setError('');
              setMode('email-input');
            }}
            className="text-sm text-purple-600 hover:text-purple-700 font-semibold"
          >
            Didn't get the code? Try again
          </button>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              sendGAEvent('auth_back_clicked', {
                from: 'otp-verify',
                source: gaSource,
              });
              setMode('email-input');
              setOtp('');
              setError('');
            }}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-bold text-gray-700 transition-all"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl font-bold text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </form>
    </>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-fade-in">
        {/* Close button - conditionally rendered */}
        {!hideCloseButton && (
          <button
            onClick={handleCloseClick}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center"
            aria-label="Close"
          >
            ×
          </button>
        )}

        {/* Content based on mode */}
        {mode === 'select' && renderSelect()}
        {mode === 'email-input' && renderEmailInput()}
        {mode === 'otp-verify' && renderOTPVerify()}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}